// Lista de questões reutilizável — sidebar do StudyWorkspace + ProvaWorkspace
// (e qualquer outro modo no futuro). Visual único; só varia o `statusOf`
// que cada modo passa (estudo tem correct/wrong/pending; prova tem
// answered/pending). Quando agrupamento = "tema" | "fonte", insere sticky
// headers entre os grupos — como categorias em playlists de rádio: o nome
// do grupo gruda no topo do scroll-container até o próximo grupo empurrá-lo
// pra fora.
//
// Caller é responsável por já ter ordenado `questoes` conforme `agrupamento`
// (usa applyAgrupamento abaixo, exportado).

import { signal } from "@preact/signals";
import { createPortal } from "preact/compat";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlignLeft,
  ArrowLeftRight,
  Clock,
  Image as ImageIcon,
  Search,
  Settings2,
  Table as TableIcon,
  X
} from "lucide-react";
import type { QuestaoStudy } from "./api";
import { AnimatedTabs } from "@app/ui/components/animated-tabs";
import { formatHMS } from "./ProvaWorkspace";
import { questaoHasImages, questaoHasTabelas } from "./questionBadges";
import {
  uiPrefs,
  toggleQuestionListSide,
  toggleQuestionListCollapsed,
  setQuestionListPreviewLines,
  setQuestionListIndicator,
  toggleQuestionListBadge,
  toggleQuestionListRowTint,
  type QuestionListBadgesPrefs,
  type QuestionListIndicator
} from "./uiPrefs";

export type QListStatus =
  | "correct"
  | "wrong"
  | "answered"
  | "pending"
  | "anulada";

// Lista sempre agrupa por tema ou fonte — não existe modo "original".
export type QListAgrupamento = "tema" | "fonte";

export interface QuestionListSidebarProps {
  questoes: QuestaoStudy[];
  currentIdx: number;
  onPick: (idx: number) => void;
  statusOf: (q: QuestaoStudy) => QListStatus;
  /** Tempo gasto (ms) por questão — quando > 0, mostra badge na linha. */
  tempoMsOf?: (q: QuestaoStudy) => number;
  agrupamento: QListAgrupamento;
  onAgrupamentoChange: (a: QListAgrupamento) => void;
  /** Ids visíveis (após filtros). Se passado, items fora do set ficam em opacity-50.
   *  Caso o caller já tenha filtrado `questoes`, pode omitir. */
  visibleIds?: Set<string>;
  /** Número exibido ao lado de cada questão. Deve ser ESTÁVEL: igual ao
   *  número da questão na ordem original do banco/prova ("fonte"), nunca
   *  recalculado por filtro/sort. Se ausente, cai pra `i + 1` (legado, pode
   *  renumerar ao filtrar — não usar em fluxos novos). */
  numOf?: (q: QuestaoStudy) => number | string;
  title?: string;
  /** Texto opcional do header tipo "12/27" pra mostrar visíveis/total. */
  countLabel?: string;
  /** Empty state customizável. */
  emptyText?: string;
  className?: string;
}

// Ordena a lista por tema ou fonte. Sort estável.
export function applyAgrupamento(
  qs: QuestaoStudy[],
  agrupamento: QListAgrupamento
): QuestaoStudy[] {
  const key = agrupamento === "tema" ? "tema" : "fonte";
  return [...qs].sort((a, b) => {
    const av = (a[key] ?? "").toString();
    const bv = (b[key] ?? "").toString();
    return av.localeCompare(bv, "pt-BR");
  });
}

// Termo de busca global da lista — compartilhado entre sidebar desktop e modal
// mobile pra que o aluno não perca o estado ao alternar. Reset manual pelo "X".
export const questionListSearch = signal<string>("");

function normalizeSearch(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim();
}

// Busca SÓ em caso, enunciado, alternativas e tabelas — nunca em resolução.
// O aluno usa isso pra achar questões enquanto estuda; a resolução é "spoiler"
// e fica fora de propósito.
function questaoMatchesSearch(q: QuestaoStudy, query: string): boolean {
  if (!query) return true;
  const haystackParts: string[] = [];
  if (typeof q.caso === "string") haystackParts.push(q.caso);
  if (typeof q.pergunta === "string") haystackParts.push(q.pergunta);
  const opts = (q as any).opcoes;
  if (Array.isArray(opts)) {
    for (const o of opts) {
      if (typeof o === "string") haystackParts.push(o);
      else if (o && typeof o === "object") {
        if (typeof o.texto === "string") haystackParts.push(o.texto);
        if (typeof o.descricao === "string") haystackParts.push(o.descricao);
      }
    }
  }
  const alts = (q as any).alternativas;
  if (Array.isArray(alts)) {
    for (const a of alts) {
      if (typeof a === "string") haystackParts.push(a);
      else if (a && typeof a === "object" && typeof a.texto === "string")
        haystackParts.push(a.texto);
    }
  }
  const tabs = (q as any).tabelas;
  if (Array.isArray(tabs)) {
    for (const t of tabs) {
      if (!t || typeof t !== "object") continue;
      if (typeof t.titulo === "string") haystackParts.push(t.titulo);
      if (typeof t.nota === "string") haystackParts.push(t.nota);
      if (Array.isArray(t.cabecalho)) {
        for (const c of t.cabecalho) if (typeof c === "string") haystackParts.push(c);
      }
      if (Array.isArray(t.linhas)) {
        for (const row of t.linhas) {
          if (Array.isArray(row)) {
            for (const c of row) if (typeof c === "string") haystackParts.push(c);
          }
        }
      }
    }
  }
  const hay = normalizeSearch(haystackParts.join(" \n "));
  return hay.includes(query);
}

// =============================================================================
// Componente principal
// =============================================================================

export function QuestionListSidebar({
  questoes,
  currentIdx,
  onPick,
  statusOf,
  tempoMsOf,
  agrupamento,
  onAgrupamentoChange,
  visibleIds,
  numOf,
  title = "Lista de questões",
  countLabel,
  emptyText = "Nada visível com os filtros atuais.",
  className
}: QuestionListSidebarProps) {
  const side = uiPrefs.value.questionListSide;
  const collapsed = uiPrefs.value.questionListCollapsed;

  // Sempre renderiza o <aside> pra permitir animação de abrir/fechar.
  // Quando colapsada, encolhe pra w-0 + opacity-0 e fica `pointer-events-none`
  // — o `overflow-hidden` esconde o conteúdo durante a transição. Pra
  // reabrir, usa o botão "Lista" no header do workspace.
  //
  // top-32 (8rem) é onde a sidebar gruda em scroll. No scroll inicial ela
  // começa MAIS BAIXO, depois do WorkspaceHeader. Pra que a página inteira
  // (top bar + breadcrumbs + workspace header + sidebar + footer) caiba
  // em 100vh sem scroll do <body>, deduzimos a soma das alturas ao redor:
  //   topbar/breadcrumbs ~4rem + main py-8 (2rem) + workspace header ~9rem
  //   + gap-5 (1.25rem) + space pro footer + footer (3rem + paddings) ≈ 21rem.
  const baseClass = `hidden overflow-hidden rounded-md border bg-card/50 transition-[width,opacity,margin,border-width] duration-300 ease-out lg:flex lg:shrink-0 lg:flex-col lg:sticky lg:top-32 ${className ?? ""}`;
  const stateClass = collapsed
    ? "lg:w-0 lg:opacity-0 lg:border-0 lg:pointer-events-none"
    : "lg:w-80 lg:opacity-100 border-border lg:h-[calc(100vh-21rem)]";

  if (collapsed) {
    return <aside class={`${baseClass} ${stateClass}`} aria-hidden />;
  }

  return (
    <aside class={`${baseClass} ${stateClass}`}>
      <header class="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 class="min-w-0 flex-1 truncate text-sm font-bold">
          {title}{" "}
          {countLabel && (
            <span class="text-xs font-medium text-muted-foreground">
              ({countLabel})
            </span>
          )}
        </h2>
        <QuestionListSettingsButton />
        <button
          type="button"
          onClick={toggleQuestionListSide}
          title={`Mover lista para ${side === "right" ? "esquerda" : "direita"}`}
          aria-label={`Mover lista para ${side === "right" ? "esquerda" : "direita"}`}
          class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ArrowLeftRight className="size-4" />
        </button>
        {/* Botão X — fecha a lista direto, sem precisar voltar no header
            do workspace pra clicar em "Lista" de novo. */}
        <button
          type="button"
          onClick={toggleQuestionListCollapsed}
          title="Esconder lista"
          aria-label="Esconder lista"
          class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>

      <div class="space-y-2 border-b border-border px-4 py-3">
        <AnimatedTabs<"tema" | "fonte">
          ariaLabel="Agrupar lista por"
          size="sm"
          variant="strong"
          className="w-full"
          items={[
            { id: "fonte", label: "Fonte" },
            { id: "tema", label: "Tema" }
          ]}
          value={agrupamento === "fonte" ? "fonte" : "tema"}
          onChange={onAgrupamentoChange}
        />
        <QuestionListSearchInput />
      </div>

      <QuestionListBody
        questoes={questoes}
        currentIdx={currentIdx}
        onPick={onPick}
        statusOf={statusOf}
        tempoMsOf={tempoMsOf}
        agrupamento={agrupamento}
        visibleIds={visibleIds}
        numOf={numOf}
        emptyText={emptyText}
      />
    </aside>
  );
}

// Input de busca compartilhado entre desktop e mobile (estado via signal global).
// Busca em caso/enunciado/alternativas/tabelas — nunca na resolução, pra
// evitar spoiler. "X" limpa instantaneamente.
export function QuestionListSearchInput() {
  const value = questionListSearch.value;
  return (
    <div class="relative">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        type="text"
        value={value}
        placeholder="Buscar nas questões…"
        aria-label="Buscar nas questões"
        onInput={(e) => {
          questionListSearch.value = (e.target as HTMLInputElement).value;
        }}
        class="h-8 w-full rounded-md border border-border bg-background py-1 pl-8 pr-7 text-xs outline-none transition-colors focus:border-primary/60 focus:ring-1 focus:ring-primary/30"
      />
      {value && (
        <button
          type="button"
          onClick={() => (questionListSearch.value = "")}
          aria-label="Limpar busca"
          title="Limpar busca"
          class="absolute right-1.5 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}

// =============================================================================
// Body (lista propriamente dita, com sticky headers). Exportado separado pra
// reuso em mobile modal / outros containers.
// =============================================================================

export function QuestionListBody({
  questoes,
  currentIdx,
  onPick,
  statusOf,
  tempoMsOf,
  agrupamento,
  visibleIds,
  numOf,
  emptyText = "Nada visível com os filtros atuais."
}: {
  questoes: QuestaoStudy[];
  currentIdx: number;
  onPick: (idx: number) => void;
  statusOf: (q: QuestaoStudy) => QListStatus;
  /** Tempo gasto na questão (ms). Quando > 0, renderiza badge de tempo
   *  na linha. Modos Estudo/Prova/Revisão passam isso conforme tracking. */
  tempoMsOf?: (q: QuestaoStudy) => number;
  agrupamento: QListAgrupamento;
  visibleIds?: Set<string>;
  /** Número estável da questão na ordem "fonte" (original do banco/prova).
   *  Se ausente, cai pra `i + 1` do array passado — o que renumera ao
   *  filtrar/ordenar. Callers novos devem sempre passar. */
  numOf?: (q: QuestaoStudy) => number | string;
  emptyText?: string;
}) {
  const ulRef = useRef<HTMLUListElement | null>(null);
  // Quando currentIdx muda (setas de navegação, mapa, clique em outro item),
  // rola o item ativo pra dentro da viewport do scroll-container. `nearest`
  // evita scroll se já estiver visível. Atraso 0ms via rAF garante que o
  // re-render do DOM aconteceu antes de medir posições.
  useEffect(() => {
    if (!ulRef.current) return;
    const el = ulRef.current.querySelector<HTMLElement>(
      `[data-question-idx="${currentIdx}"]`
    );
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  }, [currentIdx, questoes.length, agrupamento]);

  // Busca textual (live). Quando há query, oculta itens não-match. Headers
  // de grupo só aparecem se o grupo tem pelo menos 1 item visível.
  const rawQuery = questionListSearch.value;
  const query = normalizeSearch(rawQuery);
  const matchMask = useMemo(() => {
    if (!query) return null;
    const arr = new Uint8Array(questoes.length);
    for (let i = 0; i < questoes.length; i++) {
      arr[i] = questaoMatchesSearch(questoes[i], query) ? 1 : 0;
    }
    return arr;
  }, [query, questoes]);
  const totalMatched =
    matchMask === null
      ? questoes.length
      : matchMask.reduce((acc, v) => acc + v, 0);

  if (questoes.length === 0) {
    return (
      <div class="min-h-0 flex-1 overflow-y-auto scroll-thin px-3 py-3">
        <p class="px-2 py-4 text-center text-xs text-muted-foreground">
          {emptyText}
        </p>
      </div>
    );
  }

  if (matchMask && totalMatched === 0) {
    return (
      <div class="min-h-0 flex-1 overflow-y-auto scroll-thin px-3 py-3">
        <p class="px-2 py-4 text-center text-xs text-muted-foreground">
          Nenhuma questão corresponde a “{rawQuery}”.
        </p>
      </div>
    );
  }

  // Calcula renderização com sticky headers entre grupos. Quando há busca
  // (matchMask) ou filtro (visibleIds), pula questões e só emite header de
  // grupo se houver pelo menos uma visível depois.
  //
  // Antes (legado pré-numOf) os itens filtrados ficavam dimmed (opacity-50)
  // pra que o Q# não fosse renumerado. Hoje numOf garante numeração estável
  // direto da ordem-fonte, então itens filtrados SOMEM da lista.
  // [[feedback Luís 2026-05-25: "itens que não deveriam estar na lista
  // continuam aparecendo"]]
  // Se nenhuma questão visível tem categoria preenchida, não mostra header
  // "(sem categoria)" — Karol 2026-05-29: feedback "fica ruim mostrar header
  // único pra lista inteira sem categoria".
  let anyNonEmptyGroup = false;
  for (let i = 0; i < questoes.length; i++) {
    if (matchMask && !matchMask[i]) continue;
    const q = questoes[i];
    if (visibleIds && !visibleIds.has(String(q.id))) continue;
    const groupRaw =
      agrupamento === "tema" ? q.tema ?? "" : q.fonte ?? "";
    if (groupRaw.trim()) {
      anyNonEmptyGroup = true;
      break;
    }
  }

  const elements: any[] = [];
  let lastGroup: string | null = null;
  for (let i = 0; i < questoes.length; i++) {
    if (matchMask && !matchMask[i]) continue;
    const q = questoes[i];
    if (visibleIds && !visibleIds.has(String(q.id))) continue;
    const groupRaw =
      agrupamento === "tema" ? q.tema ?? "" : q.fonte ?? "";
    const group = groupRaw.trim() || "(sem categoria)";
    if (anyNonEmptyGroup && group !== lastGroup) {
      elements.push(
        <li
          key={`group-${i}-${group}`}
          class="sticky top-0 z-10 -mx-3 mb-1.5 mt-2.5 first:mt-0 border-y border-primary/40 bg-background px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-primary shadow-md"
          style={{
            backgroundImage:
              "linear-gradient(to right, hsl(var(--primary) / 0.18), hsl(var(--primary) / 0.10))"
          }}
        >
          {group}
        </li>
      );
      lastGroup = group;
    }
    elements.push(
      <li key={q.id}>
        <QuestionListItem
          q={q}
          num={numOf ? numOf(q) : i + 1}
          listIdx={i}
          active={i === currentIdx}
          status={statusOf(q)}
          tempoMs={tempoMsOf ? tempoMsOf(q) : 0}
          filtered={false}
          onClick={() => onPick(i)}
        />
      </li>
    );
  }

  return (
    // key={agrupamento} → força re-mount ao trocar tema/fonte, disparando
    // a animação de entrada (slide-in) de novo. Sem isso, Preact reusa os
    // elementos e a troca é instantânea sem transição.
    // scroll-padding-top reserva espaço pra que `scrollIntoView({block:
    // "nearest"})` não posicione o item ativo embaixo do header sticky. O
    // valor (~2.75rem) é a altura aproximada do header de grupo (py-2 +
    // text-[11px] + borders). Sem isso a questão fica meio coberta pela
    // categoria.
    <ul
      ref={ulRef}
      key={agrupamento}
      class="min-h-0 flex-1 space-y-0.5 overflow-y-auto scroll-thin px-3 py-3 animate-in fade-in slide-in-from-right-4 duration-300 [scroll-padding-top:2.75rem]"
    >
      {elements}
    </ul>
  );
}

// =============================================================================
// Item
// =============================================================================

function QuestionListItem({
  q,
  num,
  listIdx,
  active,
  status,
  tempoMs,
  filtered,
  onClick
}: {
  q: QuestaoStudy;
  /** Número EXIBIDO ao lado da questão. Pode ser texto ("42a"), pra suportar
   *  numero_original com sufixos. NÃO usar como índice — é só display. */
  num: number | string;
  /** Posição no array atual (sortedQuestoes). Usado SÓ pra scroll-into-view
   *  e estado `active`. Não aparece na UI. */
  listIdx: number;
  active: boolean;
  status: QListStatus;
  tempoMs?: number;
  filtered: boolean;
  onClick: () => void;
}) {
  const previewLines = uiPrefs.value.questionListPreviewLines;
  const indicator = uiPrefs.value.questionListIndicator;
  const badges = uiPrefs.value.questionListBadges;
  const preview =
    previewLines > 0
      ? (q.pergunta ?? "")
          .replace(/\$+/g, "")
          .replace(/\*+/g, "")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 200)
      : "";

  // Cor do indicador (stripe ou dot). Mantém paleta consistente com legado.
  let indicatorColor = "bg-muted-foreground/30";
  if (status === "anulada") indicatorColor = "bg-amber-500";
  else if (status === "correct") indicatorColor = "bg-emerald-500";
  else if (status === "wrong") indicatorColor = "bg-red-500";
  else if (status === "answered") indicatorColor = "bg-primary";

  // Tint visual do row INTEIRO por status — reforça leitura rápida sem
  // precisar olhar pra strip/dot. OPT-IN via pref questionListRowTint.
  // Aluno escolhe entre lista limpa (legado) e lista pintada por status.
  // [[feedback Luís 2026-05-25: "configuração pra escolher tint ou sem tint"]]
  const tintOn = uiPrefs.value.questionListRowTint;
  let rowTintClass = "";
  let rowTintHoverClass = "hover:bg-accent/60";
  let numColorClass = "text-foreground";
  if (tintOn) {
    if (status === "correct") {
      rowTintClass = "bg-emerald-500/[0.08] border-emerald-500/30";
      rowTintHoverClass = "hover:bg-emerald-500/15";
      numColorClass = "text-emerald-700 dark:text-emerald-300";
    } else if (status === "wrong") {
      rowTintClass = "bg-red-500/[0.08] border-red-500/30";
      rowTintHoverClass = "hover:bg-red-500/15";
      numColorClass = "text-red-700 dark:text-red-300";
    } else if (status === "anulada") {
      rowTintClass = "bg-amber-500/[0.08] border-amber-500/30";
      rowTintHoverClass = "hover:bg-amber-500/15";
      numColorClass = "text-amber-700 dark:text-amber-300";
    } else if (status === "answered") {
      rowTintClass = "bg-primary/[0.06] border-primary/25";
      rowTintHoverClass = "hover:bg-primary/15";
      numColorClass = "text-primary";
    }
  }

  const hasCaso = badges.caso && !!q.caso;
  const hasImg = badges.imagem && questaoHasImages(q);
  const hasTab = badges.tabela && questaoHasTabelas(q);
  const isAnul = badges.anulada && status === "anulada";
  const dif = q.dificuldade != null ? Number(q.dificuldade) : 0;
  const hasDif = badges.dificuldade && dif >= 1;
  const hasAnyBadge = hasCaso || hasImg || hasTab || isAnul || hasDif;

  const previewClampClass =
    previewLines === 1
      ? "line-clamp-1"
      : previewLines === 2
        ? "line-clamp-2"
        : previewLines === 3
          ? "line-clamp-3"
          : "";

  return (
    <button
      type="button"
      onClick={onClick}
      data-question-idx={listIdx}
      class={`group flex w-full items-stretch gap-2.5 rounded-md border px-2.5 py-2 text-left transition-colors ${
        active
          ? "border-primary/60 bg-primary/15 ring-1 ring-primary/30"
          : `${rowTintClass || "border-transparent"} ${rowTintHoverClass}`
      } ${filtered ? "opacity-50" : ""}`}
    >
      {indicator === "stripe" && (
        <span
          class={`w-1 shrink-0 self-stretch rounded-full ${indicatorColor}`}
          aria-hidden
        />
      )}
      <span class="flex min-w-0 flex-1 flex-col gap-1">
        <span class="flex items-center gap-1.5 text-sm">
          {indicator === "dot" && (
            <span
              class={`size-2 shrink-0 rounded-full ${indicatorColor}`}
              aria-hidden
            />
          )}
          <span class={`font-bold ${numColorClass}`}>Q{num}</span>
          {q.tema && (
            <span class="truncate text-muted-foreground">{q.tema}</span>
          )}
          {badges.tempo && tempoMs != null && tempoMs > 0 && (
            <span
              class="ml-auto inline-flex shrink-0 items-center gap-0.5 tabular-nums text-[10px] text-muted-foreground/80"
              title="Tempo gasto"
            >
              <Clock width="10" height="10" />
              {formatHMS(tempoMs)}
            </span>
          )}
        </span>
        {previewLines > 0 && (
          <span class={`${previewClampClass} text-xs text-muted-foreground/80`}>
            {preview}
          </span>
        )}
        {hasAnyBadge && (
          <span class="flex flex-wrap items-center gap-1 pt-0.5 text-xs">
            {hasDif && <MiniDifficulty value={dif} />}
            {hasCaso && (
              <span
                class="inline-flex items-center justify-center rounded-sm bg-sky-500/15 text-sky-600 dark:text-sky-400"
                style={{ width: "1.4em", height: "1.4em" }}
                title="Situação"
              >
                <AlignLeft width="1em" height="1em" strokeWidth={2.25} />
              </span>
            )}
            {hasImg && (
              <span
                class="inline-flex items-center justify-center rounded-sm bg-violet-500/15 text-violet-600 dark:text-violet-400"
                style={{ width: "1.4em", height: "1.4em" }}
                title="Imagem"
              >
                <ImageIcon width="1em" height="1em" strokeWidth={2.25} />
              </span>
            )}
            {hasTab && (
              <span
                class="inline-flex items-center justify-center rounded-sm bg-teal-500/15 text-teal-600 dark:text-teal-400"
                style={{ width: "1.4em", height: "1.4em" }}
                title="Tabela"
              >
                <TableIcon width="1em" height="1em" strokeWidth={2.25} />
              </span>
            )}
            {isAnul && (
              <span
                class="inline-flex items-center justify-center rounded-sm bg-amber-500/20 px-1.5 text-[0.7em] font-bold leading-none text-amber-600 dark:text-amber-400"
                style={{ height: "1.4em" }}
                title="Anulada"
              >
                ANUL
              </span>
            )}
          </span>
        )}
      </span>
    </button>
  );
}

// =============================================================================
// Configurações da lista — popover acionado por ícone de engrenagem no header.
// Permite ajustar quantas linhas do enunciado aparecem na prévia, o estilo do
// indicador de status (stripe vertical / bolinha / nenhum) e quais badges
// (caso, imagem, tabela, dificuldade, anulada) ficam visíveis.
// =============================================================================

function QuestionListSettingsButton() {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(
    null
  );
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  // Portal pra body — sidebar tem overflow-hidden por causa da animação
  // de colapsar, então um popover absoluto dentro dela é clipado nas
  // bordas. Renderizar no body via portal escapa o overflow.
  function updateCoords() {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setCoords({
      top: r.bottom + 4,
      right: window.innerWidth - r.right
    });
  }

  useEffect(() => {
    if (!open) return;
    updateCoords();
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (btnRef.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onScroll() {
      updateCoords();
    }
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open]);
  const prefs = uiPrefs.value;
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Preferências da lista"
        aria-label="Preferências da lista"
        aria-expanded={open ? "true" : "false"}
        class={`grid size-7 shrink-0 place-items-center rounded-md transition-colors hover:bg-accent hover:text-foreground ${
          open ? "bg-accent text-foreground" : "text-muted-foreground"
        }`}
      >
        <Settings2 className="size-4" />
      </button>
      {open &&
        coords &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            aria-label="Preferências da lista"
            class="fixed z-[80] w-64 rounded-md border border-border bg-popover p-3 text-xs shadow-2xl animate-in fade-in zoom-in-95 duration-150"
            style={{ top: `${coords.top}px`, right: `${coords.right}px` }}
          >
            <div class="space-y-3">
              <PrefBlock title="Indicador de status">
                <AnimatedTabs<QuestionListIndicator>
                  ariaLabel="Indicador de status"
                  size="sm"
                  className="w-full"
                  items={[
                    { id: "stripe", label: "Barra" },
                    { id: "dot", label: "Bolinha" },
                    { id: "none", label: "Nenhum" }
                  ]}
                  value={prefs.questionListIndicator}
                  onChange={setQuestionListIndicator}
                />
              </PrefBlock>

              <PrefBlock title="Linhas de prévia">
                <AnimatedTabs<"0" | "1" | "2" | "3">
                  ariaLabel="Linhas de prévia"
                  size="sm"
                  className="w-full"
                  items={[0, 1, 2, 3].map((n) => ({
                    id: String(n) as "0" | "1" | "2" | "3",
                    label: String(n)
                  }))}
                  value={String(prefs.questionListPreviewLines) as "0" | "1" | "2" | "3"}
                  onChange={(v) => setQuestionListPreviewLines(Number(v) as 0 | 1 | 2 | 3)}
                />
              </PrefBlock>

              <PrefBlock title="Cor de fundo por status">
                <AnimatedTabs<"on" | "off">
                  ariaLabel="Cor de fundo do item por status"
                  size="sm"
                  className="w-full"
                  items={[
                    { id: "off", label: "Sem tint" },
                    { id: "on", label: "Com tint" }
                  ]}
                  value={prefs.questionListRowTint ? "on" : "off"}
                  onChange={(v) => {
                    if ((v === "on") !== prefs.questionListRowTint) {
                      toggleQuestionListRowTint();
                    }
                  }}
                />
              </PrefBlock>

              <PrefBlock title="Badges">
                <div class="grid grid-cols-2 gap-1.5">
                  <BadgeToggle
                    label="Caso"
                    active={prefs.questionListBadges.caso}
                    onClick={() => toggleQuestionListBadge("caso")}
                  />
                  <BadgeToggle
                    label="Imagem"
                    active={prefs.questionListBadges.imagem}
                    onClick={() => toggleQuestionListBadge("imagem")}
                  />
                  <BadgeToggle
                    label="Tabela"
                    active={prefs.questionListBadges.tabela}
                    onClick={() => toggleQuestionListBadge("tabela")}
                  />
                  <BadgeToggle
                    label="Dificuldade"
                    active={prefs.questionListBadges.dificuldade}
                    onClick={() => toggleQuestionListBadge("dificuldade")}
                  />
                  <BadgeToggle
                    label="Anulada"
                    active={prefs.questionListBadges.anulada}
                    onClick={() => toggleQuestionListBadge("anulada")}
                  />
                  <BadgeToggle
                    label="Tempo"
                    active={prefs.questionListBadges.tempo}
                    onClick={() => toggleQuestionListBadge("tempo")}
                  />
                </div>
              </PrefBlock>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

function PrefBlock({
  title,
  children
}: {
  title: string;
  children: any;
}) {
  return (
    <div>
      <div class="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function IndicatorChip({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active ? "true" : "false"}
      class={`flex-1 rounded px-2 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "bg-accent text-foreground shadow-sm"
          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function BadgeToggle({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active ? "true" : "false"}
      class={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${
        active
          ? "border-primary/40 bg-primary/10 text-foreground"
          : "border-border bg-background text-muted-foreground hover:bg-accent/60"
      }`}
    >
      {label}
    </button>
  );
}

// Dificuldade compacta — 5 estrelinhas em 8x8 sem moldura, pra caber numa
// linha de badges densos. Mantém paleta do legado (âmbar pra preenchida,
// outline pra vazia).
function MiniDifficulty({ value }: { value: number }) {
  const n = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <span title={`Dificuldade ${n}/5`} class="inline-flex items-center gap-px">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg
          key={i}
          width="8"
          height="8"
          viewBox="0 0 24 24"
          fill={i < n ? "#f59e0b" : "none"}
          stroke={i < n ? "#f59e0b" : "currentColor"}
          stroke-width="3"
          stroke-linejoin="round"
          stroke-linecap="round"
          class={i < n ? "" : "text-muted-foreground/40"}
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
}

function SortButton({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active ? "true" : "false"}
      class={`flex-1 rounded px-2 py-1 font-medium transition-colors ${
        active
          ? "bg-accent text-foreground shadow-sm"
          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}
